import { FieldArray, Form, Formik } from "formik";
import React, { useCallback, useRef, useState } from "react";
import { array, boolean, number, object, string, ValidationError } from "yup";
import { type EnvVar, type MediaFile, type Test } from "../../utils/types";
import {
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import {
  AddCircle,
  AttachFile,
  Help,
  RemoveCircle,
  VideoFile,
  Image as ImageIcon,
} from "@mui/icons-material";
import FormikTextInput from "../../formikComponents/formik-text-input";
import { customStorage } from "@/firebase";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_FILE_TYPES = "image/*,video/*";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileMediaType(file: File): "image" | "video" {
  return file.type.startsWith("video") ? "video" : "image";
}

const TestEditorSettings = ({
  test,
  formRef,
  setIsDirty,
  envVars,
  organisationId,
  mediaFiles,
  onMediaFilesChange,
  newTestId,
}: {
  test?: Test;
  formRef: any;
  setIsDirty: any;
  envVars: EnvVar[];
  organisationId: string;
  mediaFiles: MediaFile[];
  onMediaFilesChange: (mediaFiles: MediaFile[]) => void;
  newTestId?: string;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFiles, setUploadingFiles] = useState<
    { name: string; progress: number }[]
  >([]);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const testId = test?.id ?? newTestId ?? "unsaved";
      const newUploadingFiles: { name: string; progress: number }[] = [];

      for (const file of Array.from(files)) {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          alert(
            `File "${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`
          );
          continue;
        }

        newUploadingFiles.push({ name: file.name, progress: 0 });
      }

      setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

      const uploadPromises = Array.from(files)
        .filter((file) => file.size <= MAX_FILE_SIZE_BYTES)
        .map(async (file) => {
          const timestamp = Date.now();
          const storagePath = `test-media/${organisationId}/${testId}/${timestamp}-${file.name}`;
          const storageRef = ref(customStorage, storagePath);

          const uploadTask = uploadBytesResumable(storageRef, file);

          return new Promise<MediaFile>((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                const progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadingFiles((prev) =>
                  prev.map((f) =>
                    f.name === file.name ? { ...f, progress } : f
                  )
                );
              },
              (error) => {
                setUploadingFiles((prev) =>
                  prev.filter((f) => f.name !== file.name)
                );
                reject(error);
              },
              async () => {
                const downloadUrl = await getDownloadURL(
                  uploadTask.snapshot.ref
                );
                setUploadingFiles((prev) =>
                  prev.filter((f) => f.name !== file.name)
                );
                resolve({
                  name: file.name,
                  storagePath,
                  downloadUrl,
                  type: getFileMediaType(file),
                  size: file.size,
                });
              }
            );
          });
        });

      const uploadedFiles = await Promise.allSettled(uploadPromises);
      const successfulUploads = uploadedFiles
        .filter(
          (result): result is PromiseFulfilledResult<MediaFile> =>
            result.status === "fulfilled"
        )
        .map((result) => result.value);

      if (successfulUploads.length > 0) {
        onMediaFilesChange([...mediaFiles, ...successfulUploads]);
        setIsDirty(true);
      }

      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [test?.id, newTestId, organisationId, mediaFiles, onMediaFilesChange, setIsDirty]
  );

  const handleRemoveFile = useCallback(
    async (index: number) => {
      const file = mediaFiles[index];
      try {
        await deleteObject(ref(customStorage, file.storagePath));
      } catch (e) {
        // File may already be deleted from storage, continue with removal from list
        console.warn("Failed to delete file from storage:", e);
      }
      const updated = mediaFiles.filter((_, i) => i !== index);
      onMediaFilesChange(updated);
      setIsDirty(true);
    },
    [mediaFiles, onMediaFilesChange, setIsDirty]
  );

  return (
    <Paper sx={{ padding: "16px", width: "100%" }}>
      <Formik
        innerRef={formRef}
        initialValues={{
          inputKeys: test?.settings?.inputKeys ?? [],
          launchParams: test?.settings?.launchParams ?? [],
          preRequests: test?.settings?.preRequests ?? [],
          teardownRequests: test?.settings?.teardownRequests ?? [],
        }}
        validationSchema={object({
          inputKeys: array(
            object({
              key: string().required("Required field"),
              defaultValue: string().notRequired(),
            }).required("Required field")
          ).default([]),
          launchParams: array(
            object({
              key: string().required("Required field"),
              value: string().required("Required field"),
            })
          ).default([]),
          preRequests: array(
            object({
              curlCommand: string().required("Required field"),
              valuesToRemember: array(
                string().required("Required field")
              ).default([]),
              delayBeforeRequest: number().required("Required field"),
            })
          ).default([]),
          teardownRequests: array(
            object({
              curlCommand: string().required("Required field"),
            })
          ).default([]),
        }).test(
          "pre-request-templates-validator",
          "check if templates values used in curl commands are previously stored",
          ({ preRequests, teardownRequests, inputKeys }, ctx) => {
            const validationErrors: ValidationError[] = preRequests.reduce<
              ValidationError[]
            >((prev, current, currentIndex, preRequestsArray) => {
              const tentativeTemplates = [
                ...(current.curlCommand?.match(/{{(.*?)}}/g) ?? []),
              ].map((match) => match.replace(/{{|}}/g, ""));
              const rememberedValues = preRequests
                .slice(0, currentIndex)
                .map((preRequest) => preRequest.valuesToRemember ?? [])
                .flat()
                .filter((value) => !!value)
                .map((value) => value.trim().split(".").at(-1));

              if (
                !tentativeTemplates.every(
                  (template) =>
                    rememberedValues.includes(template) ||
                    inputKeys
                      .map((inputKey) => inputKey.key)
                      .includes(template) ||
                    envVars
                      .map((envVar) => `env.${envVar.key}`)
                      .includes(template)
                )
              ) {
                return [
                  ...prev,
                  ctx.createError({
                    path: `preRequests.${currentIndex}.curlCommand`,
                    message: `Curl command contains template or test input value that is not previously stored`,
                  }),
                ];
              }
              return prev;
            }, []);

            const allRememberedValues = preRequests
              .flatMap((preRequest) => preRequest.valuesToRemember ?? [])
              .filter((value) => !!value)
              .map((value) => value.trim().split(".").at(-1));

            const teardownValidationErrors: ValidationError[] = (
              teardownRequests ?? []
            ).reduce<ValidationError[]>((prev, current, currentIndex) => {
              const tentativeTemplates = [
                ...(current.curlCommand?.match(/{{(.*?)}}/g) ?? []),
              ].map((match) => match.replace(/{{|}}/g, ""));

              if (
                !tentativeTemplates.every(
                  (template) =>
                    allRememberedValues.includes(template) ||
                    inputKeys
                      .map((inputKey) => inputKey.key)
                      .includes(template) ||
                    envVars
                      .map((envVar) => `env.${envVar.key}`)
                      .includes(template)
                )
              ) {
                return [
                  ...prev,
                  ctx.createError({
                    path: `teardownRequests.${currentIndex}.curlCommand`,
                    message: `Curl command contains template or test input value that is not previously stored`,
                  }),
                ];
              }
              return prev;
            }, []);

            // New validation rule: Validate environment variables in inputKeys.defaultValue
            const defaultValueErrors: ValidationError[] = inputKeys.reduce<
              ValidationError[]
            >((prev, current, currentIndex) => {
              if (!current.defaultValue) return prev;

              const templates = [
                ...(current.defaultValue.match(/{{(.*?)}}/g) ?? []),
              ].map((match) => match.replace(/{{|}}/g, ""));
              console.log({ templates });

              if (
                templates.length > 0 &&
                !templates.every((template) =>
                  envVars
                    .map((envVar) => `env.${envVar.key}`)
                    .includes(template)
                )
              ) {
                return [
                  ...prev,
                  ctx.createError({
                    path: `inputKeys.${currentIndex}.defaultValue`,
                    message: `Default value contains environment variable that is not defined`,
                  }),
                ];
              }
              return prev;
            }, []);

            const allErrors = [
              ...validationErrors,
              ...teardownValidationErrors,
              ...defaultValueErrors,
            ];
            if (allErrors.length > 0) {
              return new ValidationError(allErrors);
            }
            return true;
          }
        )}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          resetForm();
          setSubmitting(false);
        }}
        validateOnBlur={true}
      >
        {({ values }) => (
          <Form>
            <div className={"flex flex-col gap-4 items-start"}>
              <div className={"flex gap-1 items-center"}>
                <p className={"text-xl"}>Test inputs</p>
              </div>
              <FieldArray
                name={"inputKeys"}
                render={(arrayHelpers) => (
                  <div className={"flex flex-col gap-4"}>
                    {values.inputKeys.map((item, index) => (
                      <div key={index} className={"flex gap-1 items-start"}>
                        <FormikTextInput
                          name={`inputKeys.${index}.key`}
                          label={"Key"}
                          placeholder={"Key"}
                          type={"text"}
                          required
                          onChange={() => {
                            setIsDirty(true);
                          }}
                        />
                        <FormikTextInput
                          name={`inputKeys.${index}.defaultValue`}
                          label={"Default Value"}
                          type={"text"}
                          onChange={() => {
                            setIsDirty(true);
                          }}
                        />
                        <IconButton
                          sx={{ marginTop: "8px" }}
                          aria-label="delete"
                          onClick={() => {
                            arrayHelpers.remove(index);
                            setIsDirty(true);
                          }}
                        >
                          <RemoveCircle />
                        </IconButton>
                      </div>
                    ))}
                    <Button
                      variant="text"
                      sx={{ alignSelf: "flex-start" }}
                      endIcon={<AddCircle />}
                      onClick={() => {
                        arrayHelpers.push("");
                        setIsDirty(true);
                      }}
                    >
                      Add input key
                    </Button>
                  </div>
                )}
              ></FieldArray>
              <div className={"flex gap-1 items-center"}>
                <p className={"text-xl"}>Launch params</p>
                <IconButton
                  target="_blank"
                  aria-label="launch params docs"
                  size="medium"
                  href={
                    "https://docs.mobileboost.io/gpt-driver-user-guide/gpt-driver-api/advanced/app-launch-parameters"
                  }
                >
                  <Help />
                </IconButton>
              </div>
              <FieldArray
                name={"launchParams"}
                render={(arrayHelpers) => (
                  <div className={"flex flex-col gap-4"}>
                    {values.launchParams.map((item, index) => (
                      <div key={index} className={"flex gap-3 items-start"}>
                        <FormikTextInput
                          name={`launchParams.${index}.key`}
                          label={"Key"}
                          placeholder={"Key"}
                          type={"text"}
                          required
                          onChange={() => {
                            setIsDirty(true);
                          }}
                        />
                        <FormikTextInput
                          name={`launchParams.${index}.value`}
                          label={"Value"}
                          placeholder={"Value"}
                          type={"text"}
                          required
                          onChange={() => {
                            setIsDirty(true);
                          }}
                        />
                        <IconButton
                          sx={{ marginTop: "8px" }}
                          aria-label="delete"
                          onClick={() => {
                            arrayHelpers.remove(index);
                            setIsDirty(true);
                          }}
                        >
                          <RemoveCircle />
                        </IconButton>
                      </div>
                    ))}
                    <Button
                      variant="text"
                      sx={{ alignSelf: "flex-start" }}
                      endIcon={<AddCircle />}
                      onClick={() => {
                        arrayHelpers.push({
                          key: "",
                          value: "",
                        });
                        setIsDirty(true);
                      }}
                    >
                      Add param
                    </Button>
                  </div>
                )}
              ></FieldArray>
              <div className={"flex gap-1 items-center"}>
                <p className={"text-xl"}>Pre-requests</p>
                <IconButton
                  target="_blank"
                  aria-label="pre requests docs"
                  size="medium"
                  href={
                    "https://docs.mobileboost.io/gpt-driver-user-guide/best-practices/network-calls#curl-network-requests-before-the-test-prompt-execution"
                  }
                >
                  <Help />
                </IconButton>
              </div>
              <FieldArray
                name={"preRequests"}
                render={(arrayHelpers) => {
                  return (
                    <div className={"flex flex-col gap-4 w-full items-stretch"}>
                      {values.preRequests.map((item, preRequestIndex) => (
                        <div
                          key={preRequestIndex}
                          className={"flex items-start w-full gap-1"}
                        >
                          <div className={"flex flex-1 gap-2"}>
                            <div className={"flex flex-col gap-3 w-full"}>
                              <FormikTextInput
                                name={`preRequests.${preRequestIndex}.curlCommand`}
                                label={"Curl command"}
                                placeholder={"Curl command"}
                                type={"text"}
                                sx={{ width: "100%" }}
                                required
                                onChange={() => {
                                  setIsDirty(true);
                                }}
                              />
                              <FieldArray
                                name={`preRequests.${preRequestIndex}.valuesToRemember`}
                                render={(valuesToRememberArrayHelpers) => {
                                  return (
                                    <div className={"flex flex-col gap-4"}>
                                      {item.valuesToRemember.map(
                                        (
                                          valueToRemember,
                                          valueToRememberIndex
                                        ) => (
                                          <div
                                            key={valueToRememberIndex}
                                            className={"flex gap-3"}
                                          >
                                            <FormikTextInput
                                              name={`preRequests.${preRequestIndex}.valuesToRemember.${valueToRememberIndex}`}
                                              label={"Value"}
                                              placeholder={"Value"}
                                              type={"text"}
                                              required
                                              onChange={() => {
                                                setIsDirty(true);
                                              }}
                                            />
                                            <IconButton
                                              sx={{ marginTop: "8px" }}
                                              aria-label="delete"
                                              onClick={() => {
                                                valuesToRememberArrayHelpers.remove(
                                                  valueToRememberIndex
                                                );
                                                setIsDirty(true);
                                              }}
                                            >
                                              <RemoveCircle />
                                            </IconButton>
                                          </div>
                                        )
                                      )}
                                      <Button
                                        variant="text"
                                        sx={{ alignSelf: "flex-start" }}
                                        endIcon={<AddCircle />}
                                        onClick={() => {
                                          valuesToRememberArrayHelpers.push("");
                                          setIsDirty(true);
                                        }}
                                      >
                                        Add value to remember
                                      </Button>
                                    </div>
                                  );
                                }}
                              ></FieldArray>
                            </div>
                          </div>
                          {preRequestIndex !== 0 && (
                            <FormikTextInput
                              name={`preRequests.${preRequestIndex}.delayBeforeRequest`}
                              label={"Delay in ms"}
                              placeholder={"Delay in ms"}
                              type={"text"}
                              required
                              className={"basis-48"}
                              onChange={() => {
                                setIsDirty(true);
                              }}
                            />
                          )}
                          <IconButton
                            sx={{ marginTop: "8px" }}
                            aria-label="delete"
                            onClick={() => {
                              arrayHelpers.remove(preRequestIndex);
                              setIsDirty(true);
                            }}
                          >
                            <RemoveCircle />
                          </IconButton>
                        </div>
                      ))}
                      <Button
                        variant="text"
                        sx={{ alignSelf: "flex-start" }}
                        endIcon={<AddCircle />}
                        onClick={() => {
                          arrayHelpers.push({
                            curlCommand: "",
                            valuesToRemember: [],
                            delayBeforeRequest:
                              values.preRequests.length > 0 ? 1000 : 0,
                          });
                          setIsDirty(true);
                        }}
                      >
                        Add Pre-Request
                      </Button>
                    </div>
                  );
                }}
              ></FieldArray>
              <div className={"flex gap-1 items-center"}>
                <p className={"text-xl"}>Teardown requests</p>
              </div>
              <FieldArray
                name={"teardownRequests"}
                render={(arrayHelpers) => {
                  return (
                    <div className={"flex flex-col gap-4 w-full items-stretch"}>
                      {values.teardownRequests.map(
                        (item, teardownRequestIndex) => (
                          <div
                            key={teardownRequestIndex}
                            className={"flex items-start w-full gap-1"}
                          >
                            <div className={"flex flex-1 gap-2"}>
                              <FormikTextInput
                                name={`teardownRequests.${teardownRequestIndex}.curlCommand`}
                                label={"Curl command"}
                                placeholder={"Curl command"}
                                type={"text"}
                                sx={{ width: "100%" }}
                                required
                                onChange={() => {
                                  setIsDirty(true);
                                }}
                              />
                            </div>
                            <IconButton
                              sx={{ marginTop: "8px" }}
                              aria-label="delete"
                              onClick={() => {
                                arrayHelpers.remove(teardownRequestIndex);
                                setIsDirty(true);
                              }}
                            >
                              <RemoveCircle />
                            </IconButton>
                          </div>
                        )
                      )}
                      <Button
                        variant="text"
                        sx={{ alignSelf: "flex-start" }}
                        endIcon={<AddCircle />}
                        onClick={() => {
                          arrayHelpers.push({
                            curlCommand: "",
                          });
                          setIsDirty(true);
                        }}
                      >
                        Add Teardown Request
                      </Button>
                    </div>
                  );
                }}
              ></FieldArray>

              {/* Media Files Section */}
              <div className={"flex gap-1 items-center"}>
                <p className={"text-xl"}>Media files</p>
              </div>
              <Typography variant="body2" color="text.secondary">
                Upload images and videos (max {MAX_FILE_SIZE_MB}MB each) to push
                to the device before the app launches.
              </Typography>
              <div className={"flex flex-col gap-2 w-full"}>
                {mediaFiles.map((file, index) => (
                  <div
                    key={`${file.storagePath}-${index}`}
                    className={
                      "flex items-center gap-2 p-2 rounded border border-gray-600"
                    }
                  >
                    {file.type === "video" ? (
                      <VideoFile fontSize="small" />
                    ) : (
                      <ImageIcon fontSize="small" />
                    )}
                    <span className={"flex-1 text-sm truncate"}>
                      {file.name}
                    </span>
                    <span className={"text-xs text-gray-400"}>
                      {formatFileSize(file.size)}
                    </span>
                    <IconButton
                      size="small"
                      aria-label="remove file"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <RemoveCircle fontSize="small" />
                    </IconButton>
                  </div>
                ))}
                {uploadingFiles.map((file) => (
                  <div
                    key={file.name}
                    className={
                      "flex items-center gap-2 p-2 rounded border border-gray-600"
                    }
                  >
                    <AttachFile fontSize="small" />
                    <span className={"flex-1 text-sm truncate"}>
                      {file.name}
                    </span>
                    <LinearProgress
                      variant="determinate"
                      value={file.progress}
                      sx={{ width: 80 }}
                    />
                  </div>
                ))}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                multiple
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
              <Button
                variant="text"
                sx={{ alignSelf: "flex-start" }}
                endIcon={<AddCircle />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFiles.length > 0}
              >
                Add media files
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Paper>
  );
};

export default TestEditorSettings;
